/**
 * Two-touch zoom handlers for timeline density, with bounded values and a small motion threshold.
 *
 * @module apps/web/src/hooks/usePinchZoom
 */

import type { TouchEvent } from 'react';
import { useRef } from 'react';

/** Zoom bounds, functional value setter, and optional gesture sensitivity supplied by the owning calendar; the hook manages touch-distance state only. */
interface PinchZoomOptions {
  /** Apply a functional zoom update against the owner’s latest value. */
  setValue: (updater: (value: number) => number) => void;
  /** Inclusive minimum allowed zoom/row-height value. */
  min: number;
  /** Inclusive maximum allowed zoom/row-height value. */
  max: number;
  /** Value change per pixel of pinch movement; defaults to 0.16. */
  sensitivity?: number;
}

/** Return the Euclidean distance between the first two active touches, or null until a two-finger gesture exists. */
function touchDistance(event: TouchEvent<HTMLElement>) {
  const first = event.touches.item(0);
  const second = event.touches.item(1);
  if (!first || !second) return null;
  return Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY);
}

/**
 * Return touch handlers that convert two-finger distance changes into functional setValue updates
 * clamped to min/max. Sensitivity defaults to 0.16; movements under two pixels are ignored, and
 * fewer than two touches reset the gesture.
 */
export function usePinchZoom({ setValue, min, max, sensitivity = 0.16 }: PinchZoomOptions) {
  const lastDistanceRef = useRef<number | null>(null);

  return {
    onTouchStart(event: TouchEvent<HTMLElement>) {
      if (event.touches.length === 2) {
        lastDistanceRef.current = touchDistance(event);
      }
    },
    onTouchMove(event: TouchEvent<HTMLElement>) {
      if (event.touches.length !== 2) return;
      const nextDistance = touchDistance(event);
      const previousDistance = lastDistanceRef.current;
      if (nextDistance == null || previousDistance == null) return;

      event.preventDefault();
      const delta = nextDistance - previousDistance;
      if (Math.abs(delta) < 2) return;
      setValue(value => Math.max(min, Math.min(max, value + delta * sensitivity)));
      lastDistanceRef.current = nextDistance;
    },
    onTouchEnd(event: TouchEvent<HTMLElement>) {
      if (event.touches.length < 2) {
        lastDistanceRef.current = null;
      }
    },
  };
}
